import type { Cents } from "../domain/money";
import type { TradeId } from "../domain/trade";
import { tradeById } from "../domain/trade";
import { addDays, dayNumber, type LoanLedger } from "./financing";
import type { DrawInput, FinancialEvent, ProjectPlan } from "./capitalPlan";

/**
 * THE PROJECT EXECUTION FORECAST (spec §75).
 *
 * One system, not two charts side by side: **construction events CAUSE
 * financial events.** The dependency chain this engine walks is
 *
 *   schedule → work happens → phase completes → payment falls due →
 *   owner pays → draw requested → draw funds → principal changes →
 *   interest accrues → loan payment falls due → cash moves → next phase
 *
 * and it continues through exit.
 *
 * WHAT THIS ENGINE DOES NOT DO is as important as what it does:
 *
 *  • It does not decide WHEN work happens — `engine/schedule.ts` (CPM) does.
 *  • It does not decide WHAT WORK COSTS — the §74 category budget does.
 *  • It does not compute interest — `computeLoanLedger` does, and the
 *    explanations below are read back OUT of that same ledger so a number
 *    on screen can never disagree with the number in the plan.
 *  • It does not invent loan terms. Where forecasting needs something a
 *    contract does not state (how long a draw takes to fund), it uses a
 *    NAMED planning assumption and says so on every event it touched.
 *
 * What it adds is the chronology: phase blocks, the payment each phase
 * triggers, the draw that reimburses it, and the explanation of every
 * figure. Pure — same inputs, same forecast.
 */

/* ------------------------------------------------------------------ *
 * Provenance (spec §75I)
 * ------------------------------------------------------------------ */

/**
 * How much weight a forecast row deserves.
 *
 *  actual      it happened; a record says so.
 *  high        follows from confirmed terms and the committed schedule.
 *  assumption  depends on a named planning assumption (payment timing,
 *              draw turnaround) that nobody has contractually promised.
 *  scenario    exists only because the user is experimenting with timing.
 */
export type ForecastBasis = "actual" | "high" | "assumption" | "scenario";

export const BASIS_LABEL: Readonly<Record<ForecastBasis, string>> = {
  actual: "ACTUAL",
  high: "HIGH CONFIDENCE",
  assumption: "PLANNING ASSUMPTION",
  scenario: "SCENARIO",
};

/** One line of "show your work" under a forecast event. */
export interface ExplanationLine {
  label: string;
  /** Money when the line is an amount; null for a plain statement. */
  amountCents: Cents | null;
  note?: string;
}

export interface ForecastExplanation {
  /** One sentence: what this event is and where it came from. */
  summary: string;
  lines: ExplanationLine[];
  /** The figure the lines must add up to, when they are amounts. */
  totalCents: Cents | null;
  /** The named assumptions this event rests on, verbatim. */
  assumptions: string[];
}

export type ForecastEventKind =
  | "closing"
  | "phase-start"
  | "phase-complete"
  | "category-payment"
  | "draw-request"
  | "draw-funded"
  | "loan-payment"
  | "loan-fees"
  | "loan-payoff"
  | "renovation-complete"
  | "listing"
  | "lease-up"
  | "refinance"
  | "sale"
  | "rental-income"
  | "operating-expense"
  | "other";

export interface ForecastEvent {
  /** Authoritative source identity for navigation; no new financial calculation. */
  sourceEvent?: Pick<FinancialEvent,"id"|"source"|"facilityId">;
  id: string;
  date: string;
  kind: ForecastEventKind;
  /** In the words of somebody running a renovation (spec §75Y). */
  title: string;
  detail: string;
  /** Positive = money in, negative = money out, 0 = a milestone. */
  amountCents: Cents;
  /** Running owner cash before and after this event. */
  cashBeforeCents: Cents;
  cashAfterCents: Cents;
  basis: ForecastBasis;
  /** The category this belongs to, when it belongs to one. */
  categoryId: TradeId | null;
  facilityId: string | null;
  explanation: ForecastExplanation;
}

/** A block of work on the timeline — one category's span in the schedule. */
export interface ForecastPhase {
  /** Authoritative category remainder, including explicit aggregate settlement. */
  remainingCents?: Cents;
  categoryId: TradeId | null;
  /** "Demolition", or the task's own name when it has no category. */
  label: string;
  start: string;
  finish: string;
  taskIds: string[];
  taskNames: string[];
  /** The category budget this phase spends against; null when none. */
  budgetCents: Cents | null;
  /** Recorded spend against it so far. */
  actualCents: Cents;
  complete: boolean;
  /** True when the phase carries no money (inspections, permits). */
  financial: boolean;
}

export interface ForecastTaskInput {
  id: string;
  name: string;
  start: string;
  finish: string;
  categoryId: TradeId | null;
  /** 9C/schedule status: 'complete' means it actually finished. */
  complete: boolean;
  /** True when the dates came from what happened, not from the plan. */
  fromActuals: boolean;
  milestone: boolean;
}

export interface ForecastCategoryInput {
  remainingCents?: Cents;
  categoryId: TradeId;
  displayName: string;
  /** The §74 CURRENT planning basis for this category. */
  budgetCents: Cents;
  actualCents: Cents;
  /** True once every piece of its work is finished (§74 completeness). */
  complete: boolean;
  basisSource: string;
}

/** A payment tranche somebody defined for a category (spec §73F/§75E). */
export interface ForecastTrancheInput {
  id: string;
  categoryId: TradeId;
  label: string;
  pctX100: number | null;
  amountCents: Cents | null;
  /** Where in the phase the money falls due. */
  anchor: "phase-start" | "phase-finish" | "date";
  date: string | null;
  offsetDays: number;
}

/** What the lender's paperwork says, and what we had to assume. */
export interface ForecastFacilityInput {
  id: string;
  name: string;
  /** 'reimbursed' pays after the work, 'advanced' before it. */
  rehabReimbursement: "reimbursed" | "advanced" | "unknown";
  rehabFacilityCents: Cents | null;
  minimumDrawCents: Cents | null;
  drawFeeCents: Cents | null;
  /** Lender's stated share of eligible renovation cost, pct-x100. */
  rehabFundingPctX100: number | null;
}

export interface ForecastAssumptions {
  /** Days between paying for work and submitting the draw request. */
  drawRequestDelayDays: number;
  /** Days between requesting a draw and the money landing. */
  drawFundingDays: number;
  /** Days between renovation finishing and the house being listed. */
  listingDays: number;
  /** Days between listing and the sale closing. */
  saleClosingDays: number;
}

export const DEFAULT_FORECAST_ASSUMPTIONS: ForecastAssumptions = {
  drawRequestDelayDays: 1,
  drawFundingDays: 3,
  listingDays: 7,
  saleClosingDays: 30,
};

export interface ForecastInputs {
  tasks: readonly ForecastTaskInput[];
  categories: readonly ForecastCategoryInput[];
  tranches: readonly ForecastTrancheInput[];
  facility: ForecastFacilityInput | null;
  /** Draws already recorded — funded ones are FACT and never re-forecast. */
  existingDraws: readonly DrawInput[];
  assumptions: ForecastAssumptions;
  /** Which assumption values the user actually set, for honest labelling. */
  assumptionsAreDefault: { drawRequestDelay: boolean; drawFunding: boolean };
  today: string;
}

/** What the forecast asks the rest of the system to plan around. */
export interface SynthesizedDraws {
  draws: DrawInput[];
  /** Every named assumption the synthesis rested on. */
  assumptions: string[];
}

/* ------------------------------------------------------------------ *
 * Phases: the schedule, grouped the way money is grouped
 * ------------------------------------------------------------------ */

export function buildPhases(inputs: ForecastInputs): ForecastPhase[] {
  const byCategory = new Map<string, ForecastPhase>();
  const budgetOf = new Map<string, ForecastCategoryInput>(
    inputs.categories.map((c) => [c.categoryId, c]),
  );

  for (const task of inputs.tasks) {
    // A task with no budget category is still a phase on the timeline —
    // it just carries no money (an inspection, a permit approval).
    const key = task.categoryId ?? `task:${task.id}`;
    const existing = byCategory.get(key);
    if (existing) {
      if (task.start < existing.start) existing.start = task.start;
      if (task.finish > existing.finish) existing.finish = task.finish;
      existing.taskIds.push(task.id);
      existing.taskNames.push(task.name);
      existing.complete = budgetOf.get(key)?.complete === true || (existing.complete && task.complete);
      continue;
    }
    const budget = task.categoryId ? budgetOf.get(task.categoryId) : undefined;
    byCategory.set(key, {
      categoryId: task.categoryId,
      label: task.categoryId ? (tradeById(task.categoryId)?.displayName ?? task.categoryId) : task.name,
      start: task.start,
      finish: task.finish,
      taskIds: [task.id],
      taskNames: [task.name],
      budgetCents: budget ? budget.budgetCents : null,
      actualCents: budget ? budget.actualCents : 0,
      remainingCents: budget?.remainingCents,
      // Explicit completion of every scoped work item is authoritative;
      // a generic, still-planned schedule task cannot reopen its budget.
      complete: budget?.complete === true || task.complete,
      financial: budget !== undefined && budget.budgetCents !== 0,
    });
  }

  // A category with a budget but NO scheduled task still has to be paid
  // for: it appears as an unscheduled phase rather than vanishing.
  for (const category of inputs.categories) {
    if (byCategory.has(category.categoryId)) continue;
    if (category.budgetCents === 0 && category.actualCents === 0) continue;
    byCategory.set(category.categoryId, {
      categoryId: category.categoryId,
      label: category.displayName,
      start: "",
      finish: "",
      taskIds: [],
      taskNames: [],
      budgetCents: category.budgetCents,
      actualCents: category.actualCents,
      remainingCents: category.remainingCents,
      complete: category.complete,
      financial: true,
    });
  }

  return [...byCategory.values()].sort(
    (a, b) => (a.start || "9999").localeCompare(b.start || "9999") || a.label.localeCompare(b.label),
  );
}

/* ------------------------------------------------------------------ *
 * Payments: what each phase's completion costs, and when (spec §75E)
 * ------------------------------------------------------------------ */

export interface PlannedPayment {
  categoryId: TradeId;
  label: string;
  date: string;
  amountCents: Cents;
  /** True when the timing came from a labelled default, not a decision. */
  assumedTiming: boolean;
  trancheId: string | null;
  /** True when this is money the owner has ALREADY paid out. */
  fromActual?: boolean;
}

/**
 * Each open category's REMAINING money, placed on the calendar.
 *
 * A tranche somebody defined wins; where none exists the forecast uses
 * the labelled default — payment when the work completes — and says so on
 * every event it produced. A category already finished and paid produces
 * nothing: its money is already history.
 */
/** The later of two dates; a blank date never wins. */
function laterOf(a: string, b: string): string {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

export function planPayments(
  phases: readonly ForecastPhase[],
  inputs: ForecastInputs,
): { payments: PlannedPayment[]; assumptions: string[] } {
  const payments: PlannedPayment[] = [];
  const assumptions: string[] = [];
  let usedDefaultTiming = false;
  const tranchesFor = new Map<string, ForecastTrancheInput[]>();
  for (const tranche of inputs.tranches) {
    const list = tranchesFor.get(tranche.categoryId) ?? [];
    list.push(tranche);
    tranchesFor.set(tranche.categoryId, list);
  }

  for (const phase of phases) {
    if (!phase.categoryId || !phase.financial || phase.budgetCents === null) continue;


    // ACTUALS SUPERSEDE PROJECTIONS: only what is still expected to be
    // paid is forecast, and a complete category forecasts nothing.
    if(phase.remainingCents!==undefined&&(!Number.isSafeInteger(phase.remainingCents)||phase.remainingCents<0))throw new Error("Derived remaining cost must be nonnegative integer cents.");
    const remaining = phase.complete ? 0 : Math.max(0, phase.remainingCents ?? (phase.budgetCents - phase.actualCents));
    if (remaining <= 0) continue;

    const finish = phase.finish || "";
    const start = phase.start || finish;
    const tranches = tranchesFor.get(phase.categoryId) ?? [];
    if (tranches.length > 0) {
      let covered = 0;
      for (const tranche of tranches) {
        const amount =
          tranche.amountCents ??
          (tranche.pctX100 !== null ? Math.round((remaining * tranche.pctX100) / 10000) : 0);
        if (amount <= 0) continue;
        covered += amount;
        const anchor =
          tranche.anchor === "date" && tranche.date
            ? tranche.date
            : tranche.anchor === "phase-start"
              ? start
              : finish;
        payments.push({
          categoryId: phase.categoryId,
          label: `${phase.label} — ${tranche.label}`,
          date: addDays(anchor || inputs.today, tranche.offsetDays),
          amountCents: amount,
          assumedTiming: false,
          trancheId: tranche.id,
        });
      }
      const rest = remaining - covered;
      if (rest > 0) {
        usedDefaultTiming = true;
        payments.push({
          categoryId: phase.categoryId,
          label: `${phase.label} — remainder`,
          date: finish || inputs.today,
          amountCents: rest,
          assumedTiming: true,
          trancheId: null,
        });
      }
    } else {
      usedDefaultTiming = true;
      payments.push({
        categoryId: phase.categoryId,
        label: `${phase.label} payment`,
        date: finish || inputs.today,
        amountCents: remaining,
        assumedTiming: true,
        trancheId: null,
      });
    }
  }

  if (usedDefaultTiming) {
    assumptions.push(
      "Payment is assumed due when the work completes. Record a payment tranche wherever the real agreement differs.",
    );
  }
  payments.sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label));
  return { payments, assumptions };
}

/* ------------------------------------------------------------------ *
 * Draws: the reimbursement chain (spec §75F)
 * ------------------------------------------------------------------ */

export interface PlannedDraw {
  categoryId: TradeId;
  label: string;
  requestDate: string;
  fundedDate: string;
  amountCents: Cents;
  feeCents: Cents;
  /** The payment(s) this draw reimburses. */
  coversLabel: string;
}

/**
 * What the lender is expected to put back, and when.
 *
 * Only what the loan actually says drives this: a REIMBURSEMENT facility
 * follows the payment, an ADVANCE precedes the work, the lender's stated
 * share limits the amount, a minimum draw holds small amounts back until
 * enough accumulates, and the remaining facility caps the total. What the
 * loan does NOT state — how many days a draw takes — is a named planning
 * assumption, never presented as a lender promise.
 *
 * Draws the user has already recorded are left completely alone: a funded
 * draw is fact and a planned one is their decision.
 */
/**
 * What the owner has already paid out of pocket that a reimbursement
 * facility still owes back. Dated no earlier than today: you cannot ask
 * a lender for money in the past, and the request is what is still to
 * come.
 */
function reimbursementClaims(inputs: ForecastInputs): PlannedPayment[] {
  const claims: PlannedPayment[] = [];
  for (const phase of buildPhases(inputs)) {
    if (!phase.categoryId || !phase.financial || phase.actualCents <= 0) continue;
    claims.push({
      categoryId: phase.categoryId,
      label: `${phase.label} — already paid`,
      date: laterOf(phase.finish, inputs.today),
      amountCents: phase.actualCents,
      assumedTiming: true,
      trancheId: null,
      fromActual: true,
    });
  }
  return claims;
}

export function planDraws(
  payments: readonly PlannedPayment[],
  inputs: ForecastInputs,
): { draws: PlannedDraw[]; assumptions: string[] } {
  const facility = inputs.facility;
  const assumptions: string[] = [];
  if (!facility) return { draws: [], assumptions };
  if (facility.rehabFacilityCents === null || facility.rehabFacilityCents <= 0) {
    return { draws: [], assumptions };
  }
  if (facility.rehabReimbursement === "unknown") {
    assumptions.push(
      `${facility.name}: the agreement does not say whether renovation draws reimburse completed work or advance it — the forecast assumes reimbursement after payment.`,
    );
  }

  // Whatever the user already recorded consumes the facility first: the
  // forecast never re-draws money the lender has already sent or that
  // somebody has already planned for themselves.
  const alreadyCommitted = inputs.existingDraws
    .filter((d) => d.status !== "rejected")
    .reduce((sum, d) => sum + (d.receivedCents ?? d.approvedCents ?? d.requestedCents ?? 0), 0);
  let remainingFacility = facility.rehabFacilityCents - alreadyCommitted;
  if (remainingFacility <= 0) return { draws: [], assumptions };

  const share = facility.rehabFundingPctX100;
  if (share !== null && share < 10000) {
    assumptions.push(
      `${facility.name} funds ${(share / 100).toFixed(0)}% of eligible renovation cost — the rest stays owner cash.`,
    );
  }
  assumptions.push(
    inputs.assumptionsAreDefault.drawFunding
      ? `Draws are assumed to fund ${inputs.assumptions.drawFundingDays} days after the request (a planning default — set the real turnaround in the forecast assumptions).`
      : `Draws fund ${inputs.assumptions.drawFundingDays} days after the request, as configured for this project.`,
  );

  const advance = facility.rehabReimbursement === "advanced";
  const draws: PlannedDraw[] = [];
  // Amounts below the minimum draw wait and travel with the next one —
  // which is exactly what a lender's minimum does in practice.
  let pending = 0;
  let pendingLabels: string[] = [];
  let pendingAnchor = "";

  const flush = (anchor: string, force: boolean) => {
    if (pending <= 0) return;
    const minimum = facility.minimumDrawCents ?? 0;
    if (!force && pending < minimum) return;
    const amount = Math.min(pending, remainingFacility);
    if (amount <= 0) {
      pending = 0;
      pendingLabels = [];
      return;
    }
    const requestDate = advance ? anchor : addDays(anchor, inputs.assumptions.drawRequestDelayDays);
    draws.push({
      categoryId: (pendingLabels[0]?.split(" — ")[0] as TradeId) ?? "other",
      label: pendingLabels.length === 1 ? `Draw — ${pendingLabels[0]}` : `Draw — ${pendingLabels.length} items`,
      requestDate,
      fundedDate: addDays(requestDate, inputs.assumptions.drawFundingDays),
      amountCents: amount,
      feeCents: facility.drawFeeCents ?? 0,
      coversLabel: pendingLabels.join(", "),
    });
    remainingFacility -= amount;
    pending = 0;
    pendingLabels = [];
  };

  /* MONEY ALREADY PAID IS STILL REIMBURSED (spec §75F). The owner's own
   * cash went out; on a reimbursement facility the lender still owes it
   * back, and dropping the claim because the cost became an actual would
   * quietly delete an inflow the owner is counting on. These claims are
   * not forecast payments — nothing leaves the account twice — and a draw
   * somebody already recorded settles them first, below.
   */
  const eligible = [...reimbursementClaims(inputs), ...payments]
    .filter((p) => p.amountCents > 0)
    .sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label));
  // A draw somebody already recorded reimburses work already paid for, so
  // it settles those claims before anything is planned against them.
  let recordedCredit = alreadyCommitted;
  for (let i = 0; i < eligible.length; i++) {
    const payment = eligible[i];
    let eligibleAmount =
      share !== null ? Math.round((payment.amountCents * share) / 10000) : payment.amountCents;
    if (payment.fromActual && recordedCredit > 0) {
      const settled = Math.min(recordedCredit, eligibleAmount);
      recordedCredit -= settled;
      eligibleAmount -= settled;
    }
    if (eligibleAmount <= 0) continue;
    pending += eligibleAmount;
    pendingLabels.push(payment.label);
    pendingAnchor = payment.date;
    // An advance funds BEFORE the work is paid for; a reimbursement after.
    flush(advance ? addDays(payment.date, -inputs.assumptions.drawFundingDays) : payment.date, false);
  }
  // Whatever is left draws at the end even if it is under the minimum:
  // the last draw of a job is settled, not stranded.
  flush(pendingAnchor || inputs.today, true);

  if (facility.minimumDrawCents !== null && facility.minimumDrawCents > 0) {
    assumptions.push(
      `Amounts below the ${facility.name} minimum draw are held and requested with the next one.`,
    );
  }
  return { draws, assumptions };
}

/**
 * The forecast's draws, in the shape `buildProjectPlan` already consumes,
 * so the EXISTING loan ledger computes every balance, accrual and payment
 * (spec §75G) — there is no second interest calculation anywhere.
 */
export function synthesizeDraws(
  payments: readonly PlannedPayment[],
  inputs: ForecastInputs,
): SynthesizedDraws {
  const planned = planDraws(payments, inputs);
  const facilityId = inputs.facility?.id ?? "";
  return {
    draws: planned.draws.map((draw, index) => ({
      id: `forecast-draw-${index + 1}`,
      facilityId,
      label: draw.label,
      status: "planned" as const,
      plannedDate: draw.fundedDate,
      fundedDate: null,
      requestedCents: draw.amountCents,
      approvedCents: null,
      receivedCents: null,
      feeCents: draw.feeCents > 0 ? draw.feeCents : null,
      taskId: null,
    })),
    assumptions: planned.assumptions,
  };
}

/* ------------------------------------------------------------------ *
 * Explanations, read out of the ledger that produced the number
 * ------------------------------------------------------------------ */

/**
 * WHY THIS LOAN PAYMENT IS THIS SIZE (spec §75C).
 *
 * The periods come straight from `computeLoanLedger`: each one is a span
 * during which the balance did not change, with the interest that accrued
 * on it. The lines therefore always add up to the payment — they ARE the
 * payment, itemised.
 */
export function explainLoanPayment(
  ledger: LoanLedger,
  paymentDate: string,
  facilityName: string,
  ratePctX100: number | null,
): ForecastExplanation {
  const payment = ledger.payments.find((p) => p.date === paymentDate);
  if (!payment) {
    return { summary: `${facilityName} payment`, lines: [], totalCents: null, assumptions: [] };
  }
  const periods = ledger.periods.filter((p) => p.from >= payment.from && p.to <= payment.to);
  const lines: ExplanationLine[] = periods
    .filter((p) => p.interestCents > 0 || p.balanceCents > 0)
    .map((p) => ({
      label: `${p.from} → ${p.to}`,
      amountCents: p.interestCents,
      note: `${dayNumber(p.to) - dayNumber(p.from)} days on a balance of ${(p.balanceCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })}`,
    }));
  const rate = ratePctX100 === null ? "an unconfirmed rate" : `${(ratePctX100 / 100).toFixed(2)}% per year`;
  return {
    summary:
      `Interest accrued from ${payment.from} to ${payment.to} at ${rate}, simple daily (ACT/365) on the balance actually outstanding each day.`,
    lines,
    totalCents: payment.interestCents,
    assumptions: ratePctX100 === null ? ["The interest rate is not recorded, so no interest is projected."] : [],
  };
}

/* ------------------------------------------------------------------ *
 * The forecast itself
 * ------------------------------------------------------------------ */

export interface LiquidityDip {
  date: string;
  cashCents: Cents;
  /** Below zero, or below the reserve the business set. */
  belowReserve: boolean;
  /** The events on and just before this date that caused it. */
  causes: Array<{ title: string; amountCents: Cents; date: string }>;
  /** The next money coming in, if any is expected. */
  nextInflow: { title: string; date: string; amountCents: Cents } | null;
}

export interface ExecutionForecast {
  events: ForecastEvent[];
  phases: ForecastPhase[];
  assumptions: string[];
  /** Cash at its lowest, and what put it there. */
  lowestCash: { date: string; cashCents: Cents } | null;
  dips: LiquidityDip[];
  /** Owner cash required at the worst moment — the executability number. */
  peakOwnerCashCents: Cents;
  renovationCompleteDate: string | null;
  exitDate: string | null;
  /** Everything the events sum to: the project's own net cash result. */
  netCents: Cents;
}

const TITLES: Readonly<Record<string, string>> = {
  acquisition: "Closing — purchase price",
  "closing-costs": "Closing costs",
  "lender-funding": "Lender purchase funding",
  "loan-fees": "Loan origination & fees",
  "lender-draw": "Draw funded",
  "draw-fee": "Draw fee",
  "renovation-actual": "Recorded cost",
  "renovation-payment": "Contractor payment",
  "renovation-adjustment": "Authorized budget adjustment",
  "loan-payment": "Loan payment",
  "loan-payoff": "Loan payoff",
  sale: "Sale proceeds",
  "refinance-proceeds": "Refinance proceeds",
  "refinance-costs": "Refinance costs",
  "rental-income": "Rent received",
  "operating-expense": "Operating costs",
};

const KIND_MAP: Readonly<Record<string, ForecastEventKind>> = {
  acquisition: "closing",
  "closing-costs": "closing",
  "lender-funding": "closing",
  "loan-fees": "loan-fees",
  "lender-draw": "draw-funded",
  "draw-fee": "draw-funded",
  "renovation-actual": "category-payment",
  "renovation-payment": "category-payment",
  "renovation-adjustment": "category-payment",
  "loan-payment": "loan-payment",
  "loan-payoff": "loan-payoff",
  sale: "sale",
  "refinance-proceeds": "refinance",
  "refinance-costs": "refinance",
  "rental-income": "rental-income",
  "operating-expense": "operating-expense",
};

const basisOf = (event: FinancialEvent): ForecastBasis => {
  if (event.actual) return "actual";
  if (event.confidence === "scenario") return "scenario";
  return event.assumption ? "assumption" : "high";
};

/**
 * Merge the plan's money with the schedule's work into one chronology.
 *
 * The financial events arrive already derived by `buildProjectPlan` —
 * ledger, actuals precedence, sale and refinance included — and this adds
 * the construction events that caused them, the running cash position,
 * and the explanation behind every figure.
 */
export function buildExecutionForecast(
  plan: ProjectPlan,
  inputs: ForecastInputs,
  options: {
    phases: readonly ForecastPhase[];
    plannedDraws: readonly PlannedDraw[];
    facilityNames: ReadonlyMap<string, string>;
    facilityRates: ReadonlyMap<string, number | null>;
    minReserveCents: Cents;
    startingCashCents: Cents;
    /** True when a phase-timing experiment is in play (spec §75O). */
    scenario: boolean;
    extraAssumptions: readonly string[];
  },
): ExecutionForecast {
  const events: ForecastEvent[] = [];
  let seq = 0;
  const add = (
    date: string,
    kind: ForecastEventKind,
    title: string,
    detail: string,
    amountCents: Cents,
    basis: ForecastBasis,
    explanation: ForecastExplanation,
    categoryId: TradeId | null = null,
    facilityId: string | null = null,
    sourceEvent?: Pick<FinancialEvent,"id"|"source"|"facilityId">,
  ) => {
    events.push({
      id: `fe-${(seq += 1)}`,
      date, kind, title, detail, amountCents,
      cashBeforeCents: 0, cashAfterCents: 0,
      basis: options.scenario && basis !== "actual" ? "scenario" : basis,
      categoryId, facilityId, explanation, ...(sourceEvent ? {sourceEvent:{id:sourceEvent.id,source:sourceEvent.source,facilityId:sourceEvent.facilityId}} : {}),
    });
  };

  /* ---- construction milestones ---- */
  for (const phase of options.phases) {
    if (!phase.start || !phase.finish) continue;
    const workBasis: ForecastBasis = phase.complete ? "actual" : "high";
    add(
      phase.start, "phase-start", `${phase.label} begins`,
      phase.taskNames.slice(0, 3).join(", "), 0, workBasis,
      {
        summary: `${phase.label} is scheduled to start on ${phase.start}.`,
        lines: phase.taskNames.map((name) => ({ label: name, amountCents: null })),
        totalCents: null,
        assumptions: [],
      },
      phase.categoryId,
    );
    add(
      phase.finish, "phase-complete", `${phase.label} complete`,
      phase.budgetCents !== null ? `Budget basis ${money(phase.budgetCents)}` : "No renovation budget behind this work",
      0, workBasis,
      {
        summary:
          phase.complete
            ? `${phase.label} is recorded as finished.`
            : `${phase.label} is scheduled to finish on ${phase.finish}. The project schedule decides this date.`,
        lines: [
          ...(phase.budgetCents !== null
            ? [{ label: "Category budget", amountCents: phase.budgetCents } as ExplanationLine]
            : []),
          ...(phase.actualCents !== 0
            ? [{ label: "Recorded so far", amountCents: phase.actualCents } as ExplanationLine]
            : []),
        ],
        totalCents: null,
        assumptions: [],
      },
      phase.categoryId,
    );
  }

  /* ---- the money, from the plan ---- */
  const drawByDate = new Map<string, PlannedDraw>();
  for (const draw of options.plannedDraws) drawByDate.set(draw.fundedDate + draw.amountCents, draw);

  for (const event of plan.events) {
    const kind = KIND_MAP[event.kind] ?? "other";
    const title = TITLES[event.kind] ?? event.label;
    const facilityName = event.facilityId ? (options.facilityNames.get(event.facilityId) ?? "") : "";
    let explanation: ForecastExplanation;
    if (event.kind === "loan-payment" && event.facilityId) {
      const ledger = plan.ledgers.get(event.facilityId);
      explanation = ledger
        ? explainLoanPayment(ledger, event.date, facilityName, options.facilityRates.get(event.facilityId) ?? null)
        : { summary: title, lines: [], totalCents: event.amountCents, assumptions: [] };
    } else if (event.kind === "lender-draw" && !event.actual) {
      const planned = drawByDate.get(event.date + Math.abs(event.amountCents));
      explanation = {
        summary: planned
          ? `Reimbursement for ${planned.coversLabel}. Requested ${planned.requestDate}, expected to fund ${planned.fundedDate}.`
          : `A planned draw against ${facilityName}.`,
        lines: planned
          ? [
              { label: "Requested", amountCents: planned.amountCents, note: planned.requestDate },
              ...(planned.feeCents > 0 ? [{ label: "Draw fee", amountCents: -planned.feeCents } as ExplanationLine] : []),
            ]
          : [{ label: "Draw", amountCents: event.amountCents }],
        totalCents: event.amountCents,
        assumptions: [],
      };
    } else {
      explanation = {
        summary: event.label,
        lines: [{ label: title, amountCents: event.amountCents }],
        totalCents: event.amountCents,
        assumptions: event.assumption ? ["This timing is a planning assumption, not a commitment."] : [],
      };
    }
    add(
      event.date, kind, title, event.label, event.amountCents, basisOf(event),
      explanation,
      (event.source.kind === "category" ? (event.source.id as TradeId) : null),
      event.facilityId,
      event,
    );
  }

  /* ---- draw REQUESTS: the paperwork moment, no money ---- */
  for (const draw of options.plannedDraws) {
    add(
      draw.requestDate, "draw-request", "Draw request submitted",
      `${money(draw.amountCents)} — ${draw.coversLabel}`, 0, "assumption",
      {
        summary: `A draw request for ${money(draw.amountCents)} covering ${draw.coversLabel}.`,
        lines: [
          { label: "Requested", amountCents: draw.amountCents },
          { label: "Expected to fund", amountCents: null, note: draw.fundedDate },
        ],
        totalCents: null,
        assumptions: [`Submitted ${inputs.assumptions.drawRequestDelayDays} day(s) after the work is paid for.`],
      },
      draw.categoryId,
    );
  }

  /* ---- renovation complete / exit markers ---- */
  const financialPhases = options.phases.filter((p) => p.finish);
  const renovationComplete =
    financialPhases.length > 0
      ? financialPhases.reduce((latest, p) => (p.finish > latest ? p.finish : latest), financialPhases[0].finish)
      : null;
  if (renovationComplete) {
    add(
      renovationComplete, "renovation-complete", "Renovation complete",
      plan.strategy === "flip" ? "The house is ready to list." : "The house is ready to lease.",
      0, "high",
      {
        summary: "The last scheduled phase finishes here — every later date follows from it.",
        lines: [], totalCents: null, assumptions: [],
      },
    );
    if (plan.strategy === "flip") {
      add(
        addDays(renovationComplete, inputs.assumptions.listingDays), "listing", "Listed for sale",
        "", 0, "assumption",
        {
          summary: `Assumed ${inputs.assumptions.listingDays} days between finishing and listing.`,
          lines: [], totalCents: null,
          assumptions: ["Listing timing is a planning assumption."],
        },
      );
    } else {
      add(
        addDays(renovationComplete, inputs.assumptions.listingDays), "lease-up", "Lease-up begins",
        "", 0, "assumption",
        {
          summary: `Assumed ${inputs.assumptions.listingDays} days between finishing and marketing the rental.`,
          lines: [], totalCents: null,
          assumptions: ["Lease-up timing is a planning assumption."],
        },
      );
    }
  }

  /* ---- order, then walk the cash ---- */
  const ORDER: Record<ForecastEventKind, number> = {
    closing: 0, "phase-start": 1, "phase-complete": 2, "category-payment": 3,
    "draw-request": 4, "draw-funded": 5, "loan-fees": 6, "loan-payment": 7,
    "renovation-complete": 8, listing: 9, "lease-up": 9, refinance: 10,
    sale: 11, "loan-payoff": 12, "rental-income": 13, "operating-expense": 14, other: 15,
  };
  events.sort(
    (a, b) => a.date.localeCompare(b.date) || ORDER[a.kind] - ORDER[b.kind] || a.id.localeCompare(b.id),
  );

  let cash = options.startingCashCents;
  const days: Array<{ date: string; cashCents: Cents; lastIndex: number }> = [];
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    event.cashBeforeCents = cash;
    cash += event.amountCents;
    event.cashAfterCents = cash;
    const open = days[days.length - 1];
    if (open && open.date === event.date) {
      open.cashCents = cash;
      open.lastIndex = i;
    } else {
      days.push({ date: event.date, cashCents: cash, lastIndex: i });
    }
  }

  /* CASH IS COUNTED AT THE END OF THE DAY (spec §73, unchanged here).
   * Closing and the lender's funding for it settle on the same day, so the
   * moment between them is not money anybody has to have on hand. The
   * chronology still shows every movement in the order it happens; only
   * the LOW POINT, the owner-cash figure and the liquidity warnings are
   * read off the day's closing balance — the same rule the Capital Plan
   * and the financing card use, so all three agree. */
  let lowest: { date: string; cashCents: Cents } | null = null;
  for (const day of days) {
    if (lowest === null || day.cashCents < lowest.cashCents) {
      lowest = { date: day.date, cashCents: day.cashCents };
    }
  }

  /* ---- liquidity: where it goes wrong, and what caused it ---- */
  const dips: LiquidityDip[] = [];
  const threshold = options.minReserveCents;
  let inDip = false;
  for (const day of days) {
    const below = day.cashCents < threshold || day.cashCents < 0;
    if (below && !inDip) {
      // Everything that spent money in the run-up to this day's close.
      const causes = events
        .slice(Math.max(0, day.lastIndex - 8), day.lastIndex + 1)
        .filter((e) => e.amountCents < 0)
        .map((e) => ({ title: e.title, amountCents: e.amountCents, date: e.date }))
        .sort((a, b) => a.amountCents - b.amountCents)
        .slice(0, 4);
      const nextInflow = events.slice(day.lastIndex + 1).find((e) => e.amountCents > 0) ?? null;
      dips.push({
        date: day.date,
        cashCents: day.cashCents,
        belowReserve: day.cashCents >= 0,
        causes,
        nextInflow: nextInflow
          ? { title: nextInflow.title, date: nextInflow.date, amountCents: nextInflow.amountCents }
          : null,
      });
      inDip = true;
    }
    if (!below) inDip = false;
  }

  const assumptions = [...new Set([...plan.assumptions, ...options.extraAssumptions])];
  return {
    events,
    phases: [...options.phases],
    assumptions,
    lowestCash: lowest,
    dips,
    peakOwnerCashCents: lowest && lowest.cashCents < options.startingCashCents
      ? options.startingCashCents - lowest.cashCents
      : 0,
    renovationCompleteDate: renovationComplete,
    exitDate: plan.exitDate,
    netCents: events.reduce((sum, e) => sum + e.amountCents, 0),
  };
}

const money = (cents: Cents): string =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

/* ------------------------------------------------------------------ *
 * Deterministic observations about the forecast (spec §75P/Q)
 * ------------------------------------------------------------------ */

export interface ForecastObservation {
  id: string;
  severity: "risk" | "note";
  title: string;
  detail: string;
}

/**
 * What the forecast itself shows, said plainly — every one of these is a
 * statement about dates and amounts already in the timeline, never a
 * guess and never a suggestion to misrepresent anything to a lender.
 */
export function observeForecast(
  forecast: ExecutionForecast,
  minReserveCents: Cents,
): ForecastObservation[] {
  const out: ForecastObservation[] = [];

  for (const dip of forecast.dips.slice(0, 3)) {
    const causes = dip.causes.map((c) => `${c.title} ${money(c.amountCents)}`).join(", ");
    out.push({
      id: `dip-${dip.date}`,
      severity: dip.belowReserve ? "note" : "risk",
      title: dip.belowReserve
        ? `Cash dips to ${money(dip.cashCents)} on ${dip.date}, under your ${money(minReserveCents)} reserve`
        : `Cash goes negative on ${dip.date} — ${money(dip.cashCents)}`,
      detail:
        `${causes}.` +
        (dip.nextInflow
          ? ` The next money in is ${dip.nextInflow.title} on ${dip.nextInflow.date} (${money(dip.nextInflow.amountCents)}).`
          : " No further money is expected in before this point."),
    });
  }

  // A payment landing before the draw that reimburses it is the single
  // most common cash surprise in a renovation — say it with the numbers.
  const payments = forecast.events.filter((e) => e.kind === "category-payment" && e.amountCents < 0);
  const draws = forecast.events.filter((e) => e.kind === "draw-funded" && e.amountCents > 0);
  for (const payment of payments) {
    const reimbursement = draws.find(
      (d) => d.categoryId === payment.categoryId && d.date > payment.date,
    );
    if (!reimbursement) continue;
    const gap = dayNumber(reimbursement.date) - dayNumber(payment.date);
    if (gap >= 3) {
      out.push({
        id: `gap-${payment.id}`,
        severity: "note",
        title: `${payment.title} is paid ${gap} days before its draw funds`,
        detail:
          `${money(Math.abs(payment.amountCents))} leaves on ${payment.date} and the reimbursing draw is expected ${reimbursement.date}. ` +
          "That gap is owner cash you need on hand in the meantime.",
      });
      break;
    }
  }

  // A loan payment before any renovation money returns.
  const firstDraw = draws[0];
  const firstPayment = forecast.events.find((e) => e.kind === "loan-payment");
  if (firstPayment && firstDraw && firstPayment.date < firstDraw.date) {
    out.push({
      id: "payment-before-draw",
      severity: "note",
      title: "Your first loan payment falls before the first renovation draw funds",
      detail:
        `${firstPayment.title} on ${firstPayment.date} (${money(Math.abs(firstPayment.amountCents))}) comes before the draw expected ${firstDraw.date}.`,
    });
  }

  return out;
}
